import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  ChatMessageEvent,
  ChatMessageEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/assistant/v1/events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';

import { ChatMessageCreatedDetail } from './ChatMessageCreatedDetail';

type ChatMessageEventDetailProps = {
  payload: string;
};

//
// ⚛️ ⚛️ ⚛️ CHAT MESSAGE EVENT DETAIL ⚛️ ⚛️ ⚛️
//
export const ChatMessageEventDetail = ({
  payload,
}: ChatMessageEventDetailProps) => {
  const t = useTranslations();

  // States
  const [showJson, setShowJson] = React.useState(false);

  // Memoized values
  const event = React.useMemo((): ChatMessageEvent | null => {
    try {
      return fromJson(ChatMessageEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const titleByEventCase: Record<NonNullable<typeof inner.case>, string> = {
    created: t.logs.chatMessageCreated,
  };

  return (
    <Col className="@ChatMessageEventDetail flex-1">
      <EventDetailContainer
        title={inner.case ? titleByEventCase[inner.case] : t.logs.unknownEvent}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        <ChatMessageEventContent inner={inner} payload={payload} />
      </EventDetailContainer>
    </Col>
  );
};

type ChatMessageEventContentProps = {
  inner: ChatMessageEvent['event'];
  payload: string;
};

//
// ⚛️ ⚛️ ⚛️ CHAT MESSAGE EVENT CONTENT ⚛️ ⚛️ ⚛️
//
const ChatMessageEventContent = ({
  inner,
  payload,
}: ChatMessageEventContentProps) => {
  const t = useTranslations();

  switch (inner.case) {
    case 'created':
      return <ChatMessageCreatedDetail event={inner.value} />;
    case undefined:
      return <JsonViewer data={payload} title={t.logs.unknownEvent} />;
  }
};
